import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  Zap,
  Users,
  FileText,
  Receipt,
  UserCog,
  Package,
  ShoppingCart,
  FolderKanban,
  TicketCheck,
  ScanBarcode,
  Globe,
  Mic,
  Files,
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
  CreditCard,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogoBrain } from '../components/Logo';
import SARSymbol from '../components/SARSymbol';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  Users, FileText, Receipt, UserCog, Package, ShoppingCart,
  FolderKanban, TicketCheck, ScanBarcode, Globe, Mic, Files
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [userCount, setUserCount] = useState(3);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState('usd');
  const [currencies, setCurrencies] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [perUserPricing, setPerUserPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    if (selectedModules.length > 0) {
      calculatePrice();
    } else {
      setPricing(null);
    }
  }, [selectedModules, userCount, billingCycle, currency]);

  const loadModules = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/subscriptions/modules`);
      setModules(res.data.modules);
      setPerUserPricing(res.data.per_user_pricing);
      setCurrencies(res.data.currencies);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load modules:', err);
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/subscriptions/calculate`, selectedModules, {
        params: { user_count: userCount, billing_cycle: billingCycle, currency }
      });
      setPricing(res.data.pricing);
    } catch (err) {
      console.error('Failed to calculate price:', err);
    }
  };

  const toggleModule = (moduleId) => {
    setSelectedModules(prev => 
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const getModulePrice = (module) => {
    const priceKey = billingCycle === 'monthly' 
      ? `price_${currency}_monthly` 
      : `price_${currency}_yearly`;
    return module[priceKey];
  };

  const currencySymbol = currencies?.symbols?.[currency] || '$';
  const freeUsers = perUserPricing?.free_users || 3;

  // Render price with SAR symbol support
  const renderPrice = (price) => {
    if (currency === 'sar') {
      return (
        <span className="inline-flex items-center gap-1">
          <SARSymbol size={16} />
          {price}
        </span>
      );
    }
    return `${currencySymbol}${price}`;
  };

  const handleStartTrial = () => {
    navigate('/signup?trial=true');
  };

  const handleGetStarted = () => {
    navigate('/signup?modules=' + selectedModules.join(','));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      {/* SEO Title */}
      <Helmet>
        <title>Pricing & Plans | FlowFeed POS</title>
      </Helmet>
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <LogoBrain size={40} animate={false} />
            <span className="text-xl font-bold">FlowFeed</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/login')}>Login</Button>
            <Button onClick={handleStartTrial}>Start Free Trial</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 text-center">
        <Badge className="mb-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0">
          <Sparkles className="h-3 w-3 mr-1" /> 7-Day Free Trial
        </Badge>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Build Your Perfect ERP
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
          Pay only for what you need. Add or remove modules anytime. 
          Start with a free trial of all features.
        </p>
        
        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-800 rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-white dark:bg-slate-700 shadow-sm' 
                  : 'text-muted-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'yearly' 
                  ? 'bg-white dark:bg-slate-700 shadow-sm' 
                  : 'text-muted-foreground'
              }`}
            >
              Yearly <span className="text-emerald-600 text-xs ml-1">Save 17%</span>
            </button>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-2xl p-2">
            {currencies?.available?.map((curr) => (
              <button
                key={curr}
                onClick={() => setCurrency(curr)}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                  currency === curr 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm' 
                    : 'text-muted-foreground hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                {curr === 'sar' ? <SARSymbol size={14} color="currentColor" /> : currencies.symbols[curr]}
                {curr.toUpperCase()}
              </button>
            )) || (
              <>
                <button
                  onClick={() => setCurrency('usd')}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                    currency === 'usd' 
                      ? 'bg-white dark:bg-slate-700 shadow-sm' 
                      : 'text-muted-foreground'
                  }`}
                >
                  $ USD
                </button>
                <button
                  onClick={() => setCurrency('sar')}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                    currency === 'sar' 
                      ? 'bg-white dark:bg-slate-700 shadow-sm' 
                      : 'text-muted-foreground'
                  }`}
                >
                  <SARSymbol size={14} /> SAR
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Module Selection */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-2xl font-bold mb-6">Select Your Modules</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {modules.map((module) => {
                const Icon = iconMap[module.icon] || Package;
                const isSelected = selectedModules.includes(module.id);
                const price = getModulePrice(module);
                
                return (
                  <Card 
                    key={module.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      isSelected 
                        ? 'ring-2 ring-primary bg-primary/5' 
                        : 'hover:border-primary/50'
                    } ${module.is_addon ? 'border-purple-200 dark:border-purple-800' : ''}`}
                    onClick={() => toggleModule(module.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            isSelected 
                              ? 'bg-primary text-white' 
                              : 'bg-slate-100 dark:bg-slate-800 text-muted-foreground'
                          }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              {module.name}
                              {module.is_popular && (
                                <Badge variant="secondary" className="text-[10px]">Popular</Badge>
                              )}
                              {module.is_addon && (
                                <Badge className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">Add-on</Badge>
                              )}
                            </h3>
                            <p className="text-sm text-muted-foreground">{module.description}</p>
                          </div>
                        </div>
                        <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'bg-primary border-primary' 
                            : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold flex items-center gap-1">
                          {renderPrice(price)}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 space-y-1">
                        {module.features.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check className="h-3 w-3 text-emerald-500" />
                            {feature}
                          </div>
                        ))}
                        {module.features.length > 3 && (
                          <p className="text-xs text-muted-foreground">
                            +{module.features.length - 3} more features
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-white">Your Plan</CardTitle>
                  <CardDescription className="text-slate-300">
                    {selectedModules.length === 0 
                      ? 'Select modules to build your plan'
                      : `${selectedModules.length} module${selectedModules.length > 1 ? 's' : ''} selected`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Selected Modules */}
                  {selectedModules.length > 0 && (
                    <div className="space-y-2">
                      {selectedModules.map(id => {
                        const module = modules.find(m => m.id === id);
                        if (!module) return null;
                        return (
                          <div key={id} className="flex items-center justify-between text-sm">
                            <span>{module.name}</span>
                            <span>{renderPrice(getModulePrice(module))}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* User Count */}
                  <div className="pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-300">Team Size</span>
                      <span className="text-sm text-emerald-400">First {freeUsers} users free</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-8 w-8 p-0 bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                        onClick={() => setUserCount(Math.max(1, userCount - 1))}
                      >
                        -
                      </Button>
                      <span className="text-2xl font-bold w-12 text-center">{userCount}</span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-8 w-8 p-0 bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                        onClick={() => setUserCount(userCount + 1)}
                      >
                        +
                      </Button>
                      <span className="text-sm text-slate-400">users</span>
                    </div>
                    {userCount > freeUsers && (
                      <p className="text-xs text-slate-400 mt-2">
                        +{userCount - freeUsers} extra user{userCount - freeUsers > 1 ? 's' : ''} @ {currencySymbol}
                        {perUserPricing?.[`${currency}_${billingCycle}`] || 10}/{billingCycle === 'monthly' ? 'mo' : 'yr'} each
                      </p>
                    )}
                  </div>

                  {/* Pricing Breakdown */}
                  {pricing && (
                    <div className="pt-4 border-t border-slate-700 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300">Modules</span>
                        <span>{currencySymbol}{pricing.module_total}</span>
                      </div>
                      {pricing.user_total > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-300">Extra Users</span>
                          <span>{currencySymbol}{pricing.user_total}</span>
                        </div>
                      )}
                      {pricing.discount_percent > 0 && (
                        <div className="flex justify-between text-sm text-emerald-400">
                          <span>Yearly Discount ({pricing.discount_percent}%)</span>
                          <span>-{currencySymbol}{pricing.discount_amount}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xl font-bold pt-2 border-t border-slate-700">
                        <span>Total</span>
                        <span>
                          {currencySymbol}{pricing.total}
                          <span className="text-sm font-normal text-slate-400">
                            /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="pt-4 space-y-3">
                    <Button 
                      className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      onClick={handleStartTrial}
                    >
                      <Sparkles className="h-5 w-5 mr-2" />
                      Start 7-Day Free Trial
                    </Button>
                    {selectedModules.length > 0 && (
                      <Button 
                        variant="outline" 
                        className="w-full bg-transparent border-slate-600 text-white hover:bg-slate-700"
                        onClick={handleGetStarted}
                      >
                        Subscribe Now <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>

                  {/* Trust Badges */}
                  <div className="pt-4 flex items-center justify-center gap-4 text-xs text-slate-400">
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3" /> Secure
                    </div>
                    <div className="flex items-center gap-1">
                      <CreditCard className="h-3 w-3" /> No CC Required
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Cancel Anytime
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* FAQ */}
              <Card className="mt-4">
                <CardContent className="p-4 space-y-3">
                  <h3 className="font-semibold">Frequently Asked</h3>
                  <div className="text-sm space-y-2">
                    <p className="text-muted-foreground">
                      <strong>Can I change modules later?</strong><br/>
                      Yes! Add or remove modules anytime.
                    </p>
                    <p className="text-muted-foreground">
                      <strong>What happens after trial?</strong><br/>
                      Choose your modules to continue. No auto-charge.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
